const STYLE_ID = 'phoenix-header-layering-fix';
const ACCOUNT_STYLE_ID = 'phoenix-account-layering-fix';
const ACCOUNT_BACKDROP_ID = 'phoenix-account-backdrop';

const ensureDocumentStyle = () => {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* Phoenix launch fix: cached transparent headers must not overlay hero/product content. */
    body:has(.header[transparent]) .content-for-layout > .shopify-section:first-child {
      margin-top: 0 !important;
    }

    #header-group > .header-section {
      position: sticky !important;
      top: 0 !important;
      z-index: 1000 !important;
      background: #fff !important;
    }

    #header-component,
    #header-component[transparent],
    .header[transparent] {
      position: relative !important;
      inset: auto !important;
      background: #fff !important;
      opacity: 1 !important;
      z-index: auto !important;
    }

    #header-component :is(.header__underlay-open, .header__underlay-closed, .header__row) {
      background: #fff !important;
    }

    html[phoenix-account-open],
    html[phoenix-account-open] body {
      overflow: hidden !important;
      overscroll-behavior: none !important;
    }

    #phoenix-account-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1900;
      background: rgba(17, 17, 17, 0.28);
      opacity: 1;
    }
  `;
  document.head.append(style);
};

const applyHeaderFix = () => {
  ensureDocumentStyle();

  const header = document.getElementById('header-component');
  const headerSection = document.querySelector('#header-group > .header-section');
  const firstSection = document.querySelector('.content-for-layout > .shopify-section:first-child');

  if (header) {
    header.removeAttribute('transparent');
    header.removeAttribute('data-transparent-color-scheme');
    header.style.background = '#fff';
    header.style.position = 'relative';
    header.style.inset = 'auto';
    header.style.opacity = '1';
  }

  if (headerSection) {
    headerSection.style.position = 'sticky';
    headerSection.style.top = '0';
    headerSection.style.zIndex = '1000';
    headerSection.style.background = '#fff';
  }

  if (firstSection) firstSection.style.marginTop = '0';

  document.body.style.setProperty('--transparent-header-offset-boolean', '0');
};

const isAccountOpen = (root) => {
  try {
    return Boolean(root.querySelector('dialog[open], [popover]:popover-open, .dialog[open], [aria-modal="true"]'));
  } catch (_error) {
    return Boolean(root.querySelector('dialog[open], .dialog[open], [aria-modal="true"]'));
  }
};

const closeAccount = (account) => {
  const root = account.shadowRoot;
  if (!root) return;

  let openElement;
  try {
    openElement = root.querySelector('dialog[open], [popover]:popover-open, .dialog[open]');
  } catch (_error) {
    openElement = root.querySelector('dialog[open], .dialog[open]');
  }

  if (openElement?.close) openElement.close();
  if (openElement?.hidePopover) openElement.hidePopover();
};

const setAccountOpen = (account, open) => {
  ensureDocumentStyle();
  document.documentElement.toggleAttribute('phoenix-account-open', open);

  let backdrop = document.getElementById(ACCOUNT_BACKDROP_ID);

  if (open && !backdrop) {
    backdrop = document.createElement('div');
    backdrop.id = ACCOUNT_BACKDROP_ID;
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.addEventListener('click', () => closeAccount(account));
    document.body.append(backdrop);
  } else if (!open && backdrop) {
    backdrop.remove();
  }
};

const injectAccountStyle = (account) => {
  const root = account.shadowRoot;
  if (!root || root.getElementById(ACCOUNT_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = ACCOUNT_STYLE_ID;
  style.textContent = `
    /* Phoenix launch fix: native customer account panel must be opaque and above the page. */
    .dialog,
    dialog,
    [popover],
    [role="dialog"],
    .panel,
    .menu {
      position: fixed !important;
      z-index: 2000 !important;
      background: #fff !important;
      background-color: #fff !important;
      opacity: 1 !important;
      color: #111 !important;
      box-shadow: 0 24px 70px rgba(17, 17, 17, 0.24) !important;
    }

    .dialog *,
    dialog *,
    [popover] *,
    [role="dialog"] * {
      opacity: 1 !important;
    }

    .dialog::backdrop,
    dialog::backdrop,
    [popover]::backdrop {
      position: fixed !important;
      inset: 0 !important;
      background: rgba(17, 17, 17, 0.28) !important;
      opacity: 1 !important;
      backdrop-filter: none !important;
    }

    @media (min-width: 751px) {
      .dialog,
      dialog,
      [popover],
      [role="dialog"] {
        width: min(360px, calc(100vw - 48px)) !important;
        max-width: min(360px, calc(100vw - 48px)) !important;
        top: calc(var(--header-height, 72px) + 16px) !important;
        inset-inline-end: 32px !important;
      }
    }

    @media (max-width: 750px) {
      .dialog,
      dialog,
      [popover],
      [role="dialog"] {
        width: min(100vw - 24px, 420px) !important;
        max-width: calc(100vw - 24px) !important;
        top: calc(var(--header-height, 64px) + 12px) !important;
        inset-inline: 12px !important;
      }
    }
  `;
  root.append(style);
};

const connectAccount = (account) => {
  if (account.__phoenixHeaderLayeringFix) return;
  account.__phoenixHeaderLayeringFix = true;

  const sync = () => {
    injectAccountStyle(account);
    if (account.shadowRoot) setAccountOpen(account, isAccountOpen(account.shadowRoot));
  };

  account.addEventListener('open', () => [0, 100, 300, 700].forEach((delay) => setTimeout(sync, delay)));
  account.addEventListener('close', () => setAccountOpen(account, false));

  if (account.shadowRoot) {
    new MutationObserver(sync).observe(account.shadowRoot, { childList: true, subtree: true, attributes: true });
  }

  sync();
};

const boot = () => {
  applyHeaderFix();
  document.querySelectorAll('shopify-account').forEach(connectAccount);
};

boot();
window.addEventListener('pageshow', boot);
window.addEventListener('resize', applyHeaderFix);
new MutationObserver(boot).observe(document.documentElement, { childList: true, subtree: true });
